/**
 * Default baby cost items — every value sourced (see docs/baby/DEFAULTS_SOURCES.md).
 *
 * Costs are in CAD (rounded to realistic retail points, ~1:1 with cited US data
 * for baby goods). Usage rates are per the ratePeriod field. No invented numbers;
 * the `source` field on each item carries the citation for AC-10 verification.
 */

import type { BabyItem } from '../shared/types';

export const DEFAULT_BABY_ITEMS: BabyItem[] = [
  // ---------------- consumables ----------------
  {
    id: 'diapers',
    label: 'Diapers',
    kind: 'consumable',
    enabled: true,
    unitCost: 0.3,
    rate: 10,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: 30,
    custom: false,
    source:
      'Good Housekeeping (Huggies ≈$0.35/diaper) + Pampers/Babylist (8–12/day newborn). '
      + 'https://www.goodhousekeeping.com/childrens-products/g19502261/best-diapers/ · '
      + 'https://www.babylist.com/hello-baby/how-many-diapers-babys-first-year',
    help:
      'Newborns go through 8–12 diapers/day (~3,000 in year one); this drops to ~5/day '
      + 'once solids start. Name brands run ~$0.30–0.35 each, store brands ~$0.20. '
      + 'Default end month 30 covers full potty-training window — adjust to your plan.',
  },
  {
    id: 'wipes',
    label: 'Baby wipes',
    kind: 'consumable',
    enabled: true,
    unitCost: 0.025,
    rate: 30,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: 30,
    custom: false,
    source:
      'Babylist wipes review (~$0.025/wipe bulk) + AlppiBaby (~750–900/month newborn). '
      + 'https://www.babylist.com/hello-baby/we-tried-all-the-baby-wipes · '
      + 'https://alppibaby.com/blogs/alppi-adventures/how-many-baby-wipes-do-i-need',
    help:
      'Bulk packs (Costco Kirkland, Amazon) land around $0.025/wipe. Newborns use '
      + '~25–30/day (~750–900/month); usage eases as they grow. Cheaper per wipe '
      + 'than diapers but adds up — the default captures the heavy-use period.',
  },
  {
    id: 'formula',
    label: 'Formula',
    kind: 'consumable',
    enabled: true,
    unitCost: 1.1,
    rate: 28,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: 6,
    custom: false,
    source:
      'Wirecutter/Milk Drunk (~$0.69–$1.57/oz) + Stanford/CDC (28–32 oz/day 2–6mo). '
      + 'https://www.nytimes.com/wirecutter/reviews/best-baby-formula/ · '
      + 'https://www.stanfordchildrens.org/en/topic/default?id=feeding-guide-for-the-first-year-90-P02209 · '
      + 'https://www.cdc.gov/infant-toddler-nutrition/formula-feeding/how-much-and-how-often.html',
    help:
      'Powdered formula: ~$0.69/oz (Kirkland) to ~$1.50/oz (Enfamil/Similac). Babies '
      + 'drink 28–32 oz/day from 2–6 months; the default ends when solids begin. '
      + 'Exclusive breastfeeding zeros this out. Hypoallergenic runs 2–3× higher.',
  },
  {
    id: 'pail-refills',
    label: 'Diaper pail refills',
    kind: 'consumable',
    enabled: true,
    unitCost: 0.55,
    rate: 0.2,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: 30,
    custom: false,
    source:
      'Diaper Genie Platinum Easy-Roll cartridges: ~$50 for 3 cartridges (30 bags each) '
      + '→ ~$0.55/bag. At ~1 bag per 5 days that is ~73 bags/year. '
      + 'https://www.walmart.com/ip/Playtex-Baby-Diaper-Genie-Diaper-Pail-Refills-3-Pk-720-Ct/17133567',
    help:
      'Proprietary refill bags cost ~$0.55 each and last ~5 days with a newborn '
      + '(~73/year, ~$40/yr). Tube-style bags from third-party sellers online are '
      + 'even cheaper. Plain kitchen garbage bags work too — set this to 0.',
  },

  // ---------------- services ----------------
  {
    id: 'daycare',
    label: 'Daycare',
    kind: 'service',
    enabled: true,
    unitCost: 1100,
    rate: 1,
    ratePeriod: 'month',
    startMonth: 6,
    endMonth: 36,
    custom: false,
    source:
      'Child Care Aware of America 2024 blended avg $13,128/yr ≈ $1,094/mo; '
      + 'US Census infant-specific $15,417/yr. Canada: CWELCC provincial agreements '
      + '(canada.ca/en/employment-social-development/campaigns/child-care.html), '
      + 'Quebec Ministère des Finances 2025/2026 daily rates, Ontario $22/day cap '
      + '(Jan 2025), BC CCFRI, Alberta $15/day flat. '
      + 'https://www.childcareaware.org/price-landscape24/ · '
      + 'https://www.census.gov/library/stories/2024/01/rising-child-care-cost.html',
    help:
      'Biggest line item for most families. The $1,100/mo default is the US average. '
      + 'Canadians pay far less under CWELCC — expand below for provincial rates.',
    details:
      'The $1,100/mo default is the US national average (CCAoA 2024). In Canada, '
      + 'subsidized fees under the CWELCC system ($10-a-day target by Mar 2026) vary '
      + 'hugely by province — set cost to your daily rate, rate to 1, pick /day:\n'
      + '• Quebec: $9.35/day subsidized (2025) → ~$284/mo\n'
      + '• Ontario: $22/day CWELCC cap (2025), dropping to $12/day in Mar 2026\n'
      + '• Alberta: $15/day flat for under-kindergarten age\n'
      + '• BC: ~$20/day avg via the Fee Reduction Initiative; some $10/day spaces\n'
      + 'Unsubsidized Canadian infant care still runs ~$1,200–$2,000/mo. Default '
      + 'start month 6 ≈ end of parental leave; end 36 ≈ preschool.',
  },

  // ---------------- one-time gear ----------------
  {
    id: 'crib',
    label: 'Crib',
    kind: 'onetime',
    enabled: true,
    unitCost: 400,
    rate: 1,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: -1,
    custom: false,
    source:
      'Wirecutter / Consumer Reports convertible-crib range $300–$700. '
      + 'https://www.nytimes.com/wirecutter/reviews/best-cribs/ · '
      + 'https://www.consumerreports.org/babies-kids/cribs/best-convertible-cribs-of-the-year-a9014068092/',
    help:
      'A convertible crib (crib → toddler bed → daybed) runs $300–$700 new; the $400 '
      + 'default sits mid-range. Bassinets ($50–$200) cover the first few months if '
      + 'you defer the crib.',
  },
  {
    id: 'breast-pump',
    label: 'Breast pump',
    kind: 'onetime',
    enabled: true,
    unitCost: 200,
    rate: 1,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: -1,
    custom: false,
    source:
      'Consumer Reports / Wirecutter double-electric $150–$350 (note: ACA usually $0). '
      + 'https://www.consumerreports.org/babies-kids/breast-pumps/best-breast-pumps-a7652309030/ · '
      + 'https://aeroflowbreastpumps.com/blog/hospital-grade-breast-pumps-faq',
    help:
      'A double-electric pump (Spectra, Medela) retails $150–$350, but under the US '
      + 'Affordable Care Act most insurance plans cover one at $0 — check your plan '
      + 'and set this to 0 if covered.',
  },
  {
    id: 'car-seat',
    label: 'Car seat',
    kind: 'onetime',
    enabled: true,
    unitCost: 250,
    rate: 1,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: -1,
    custom: false,
    source: 'Babylist tiered guide $150–$300 mid-range. https://www.babylist.com/hello-baby/how-much-do-car-seats-cost',
    help:
      'Non-negotiable — hospitals won\'t discharge without one. Infant-only seats '
      + '($150–$300) detach from a base; convertible seats ($200–$400) last birth '
      + 'through toddlerhood. Never buy used — expiry dates and crash history matter.',
  },
  {
    id: 'stroller',
    label: 'Stroller',
    kind: 'onetime',
    enabled: true,
    unitCost: 400,
    rate: 1,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: -1,
    custom: false,
    source: 'Babylist sweet spot $300–$500. https://www.babylist.com/hello-baby/how-much-do-strollers-cost',
    help:
      'Everyday full-size strollers sit in the $300–$500 sweet spot. Travel systems '
      + '(stroller + infant car seat) bundle both for ~$400–$700 and can replace this '
      + 'and the car seat line. Umbrella strollers ($50–$100) are a cheap secondary '
      + 'option for quick trips.',
  },
];

/** Default window: first 24 months. */
export const DEFAULT_WINDOW_START = 0;
export const DEFAULT_WINDOW_END = 24;

/** Generate a unique id for a user-added custom item. */
export function newCustomId(): string {
  return `custom-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}
